"use client"

import * as React from "react"
import { useLocation, useNavigate, type Location } from "react-router-dom"

/**
 * True when the entry behind this one is a screen of ours, so `navigate(-1)` never leaves the app.
 *
 * React Router numbers the entries it pushes (`idx` in history.state): above 0 there is a screen of ours behind
 * this one. The location key alone is not enough: a replace (a tab switch, the app's own redirect on load) gives a
 * directly opened screen a real key with nothing of ours behind it.
 */
export function hasInAppHistory(location: Pick<Location, "key">): boolean {
  if (location.key === "default") return false
  const idx = (window.history.state as { idx?: unknown } | null)?.idx
  return typeof idx === "number" ? idx > 0 : true
}

export interface CreatePageExit {
  /** Cancel / close: back to where the user came from, else (opened directly) replace this page with `listHref`. */
  cancel: () => void
  /** After a successful save: replace this page with `to` (default `listHref`), so Back skips the spent form. */
  finish: (to?: string) => void
}

/**
 * The one exit rule for a full-page create (or edit) form. The form page never stays in history once it is left:
 * a saved form replaces itself with the record (or the list), and cancelling steps back instead of pushing the list.
 */
export function useCreatePageExit(listHref: string): CreatePageExit {
  const navigate = useNavigate()
  const location = useLocation()

  const cancel = React.useCallback(() => {
    if (hasInAppHistory(location)) {
      navigate(-1)
      return
    }
    navigate(listHref, { replace: true })
  }, [listHref, location, navigate])

  const finish = React.useCallback(
    (to?: string) => {
      navigate(to ?? listHref, { replace: true })
    },
    [listHref, navigate],
  )

  return React.useMemo(() => ({ cancel, finish }), [cancel, finish])
}
